'use client';

import type React from 'react';
import { useState, useRef, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@skybeam/shared-utils';

import { Button } from '@simulmedia/ui-kit';

const CARD_WIDTH = 343;
const CARD_GAP = 14;

export type CardType = {
  content: string;
  icon: React.ReactNode;
  title: string;
};

interface CardSliderProps {
  data: CardType[];
}

const Card: React.FC<CardType> = ({ content, icon, title }) => {
  return (
    <div className="laptop:h-[343px] laptop:min-w-[343px] tablet:h-[252px] tablet:min-w-[252px] laptop:p-8 laptop:max-w-[343px] flex h-[192px] max-w-[360px] flex-col justify-between overflow-hidden rounded-lg border border-gray-200 bg-white p-[26px]">
      <div className="laptop:gap-x-[22px] tablet:gap-x-[18px] flex items-center gap-x-[14px]">
        <div className="laptop:p-[15px] tablet:p-3 rounded-lg bg-blue-100 p-1.5">
          {icon}
        </div>
        <div className="laptop:text-3xl laptop:min-w-auto text-xl leading-[114%] font-medium tracking-[-0.29px]">
          {title}
        </div>
      </div>
      <div className="laptop:text-base text-sm">{content}</div>
    </div>
  );
};

export const CardSlider: React.FC<CardSliderProps> = ({ data }) => {
  const [disablePrev, setDisablePrev] = useState(true);
  const [disableNext, setDisableNext] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);

  const EPS = 2; // tolerate tiny float errors

  const recomputeButtons = () => {
    const el = containerRef.current;
    if (!el) return;
    const { clientWidth, scrollLeft, scrollWidth } = el;
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);

    if (maxScrollLeft <= EPS) {
      setDisablePrev(true);
      setDisableNext(true);
      return;
    }

    setDisablePrev(scrollLeft <= EPS);
    setDisableNext(maxScrollLeft - scrollLeft <= EPS);
  };

  const slide = (direction: 'left' | 'right') => {
    if (!containerRef.current) return;
    const { clientWidth, scrollLeft, scrollWidth } = containerRef.current;
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);

    if (maxScrollLeft <= EPS) return;

    const step = 1.2 * (CARD_WIDTH + CARD_GAP);

    const target =
      direction === 'right'
        ? Math.min(scrollLeft + step, maxScrollLeft)
        : Math.max(scrollLeft - step, 0);

    containerRef.current.scrollTo({ left: target, behavior: 'smooth' });
  };

  const handleScroll = () => {
    recomputeButtons();
  };

  useEffect(() => {
    recomputeButtons();

    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => recomputeButtons());
    ro.observe(el);

    const mo = new MutationObserver(() => recomputeButtons());
    mo.observe(el, { childList: true, subtree: true });

    const onWinResize = () => recomputeButtons();
    window.addEventListener('resize', onWinResize);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', onWinResize);
    };
  }, [recomputeButtons]);

  return (
    <div className="desktop:gap-y-8 laptop:gap-y-6 tablet:max-w-[520px] laptop:max-w-full relative flex flex-col">
      <div
        ref={containerRef}
        className="overflow-hidden"
        onScroll={handleScroll}
      >
        <div className="laptop:flex tablet:grid-cols-2 laptop:gap-[14px] tablet:gap-4 grid grid-cols-1 gap-3">
          {data.map((cardProps) => (
            <Card key={cardProps.title} {...cardProps} />
          ))}
          <div className="laptop:block hidden" />
        </div>
      </div>
      <div
        className={cn(
          'laptop:flex hidden justify-end gap-x-4 pr-14 min-[2200px]:hidden',
          disablePrev && disableNext ? 'invisible' : '',
        )}
      >
        <Button
          className={`${disablePrev ? 'invisible' : ''}`}
          onClick={() => slide('left')}
        >
          <ChevronRight className="rotate-180" />
        </Button>
        <Button
          className={`${disableNext ? 'invisible' : ''}`}
          onClick={() => slide('right')}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
};
